import { CreateBookingParams, useCreateBooking, useEditBooking } from '@carlito/bookito-client';
import { BookingParticipant, BookitoBookingFormValues } from '../shared/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { BookingStepView, BookingStepViews, DefaultEventBookingFlow } from '../shared/constants';
import { useGetHotelConfiguration } from '@carlito/hub-react';
import { useRegister } from '@carlito/api';
import { analyticsEvent } from '@carlito/app-analytics';

type useBookingFormProps = {
  initialEditData?: Partial<BookitoBookingFormValues>;
  bookingId?: string;
  onCreateSuccess?: () => void;
  eventId: string;
  bookingSource: CreateBookingParams['bookingSource'];
  isEditMode?: boolean;
  hotelId: string;
};

export const useBookingForm = ({
  initialEditData,
  bookingSource,
  eventId,
  onCreateSuccess,
  isEditMode = false,
  bookingId,
  hotelId,
}: useBookingFormProps) => {
  const formikRef = useRef<FormikProps<BookitoBookingFormValues>>(null);
  const [fieldError, setFieldError] = useState<string>('');
  const [steps, setSteps] = useState<BookingStepView[]>(DefaultEventBookingFlow);
  const [isGuestLoggedIn, setIsGuestLoggedIn] = useState<boolean>(false);

  const { hasPms: hasPmsEnabled, supportedPmsFeatures } = useGetHotelConfiguration({ hotelId });
  const [allParticipants, setAllParticipants] = React.useState<BookingParticipant[]>([]);
  const [currentStep, setCurrenStep] = useState<BookingStepView>(
    isEditMode ? BookingStepViews.GuestExtra : DefaultEventBookingFlow[0]
  );
  const [successMsg, updateSuccessMsg] = useState('the-event-was-booked-successfully');

  const initStep = () => {
    if (isEditMode) {
      setCurrenStep(BookingStepViews.GuestExtra);
    } else {
      if (initialEditData?.bookedForDate) {
        setCurrenStep(BookingStepViews.SlotPicker);
      } else {
        setCurrenStep(DefaultEventBookingFlow[0]);
      }
    }
  };

  const resetCurrentStep = () => {
    initStep();
  };

  useEffect(() => {
    initStep();
  }, [initialEditData?.bookedForDate]);

  const initialValues: BookitoBookingFormValues = useMemo(() => {
    return {
      ...initialEditData,
      comment: initialEditData?.comment ?? '',
      bookedForDate: initialEditData?.bookedForDate ?? undefined,
      participants: initialEditData?.participants ?? [],
      numOfAdditionalParticipants: initialEditData?.numOfAdditionalParticipants ?? 0,
    };
  }, [initialEditData]);

  const {
    mutate: createBooking,
    isLoading: isCreatingBooking,
    error: createError,
    isError: isCreateError,
    isSuccess: isCreateSuccess,
    reset: resetCreateMutation,
  } = useCreateBooking();

  const {
    mutate: editBooking,
    isLoading: editIsLoading,
    error: editError,
    isError: editIsError,
    isSuccess: editIsSuccess,
    reset: resetEditBooking,
  } = useEditBooking();

  const logAnalyticEvent = (meta: Record<string, unknown>) => {
    const analyticSource = {
      app: 'guest_app',
      dashboard: 'hotel_dashboard',
      web_widget: 'web_widget',
      kiosk: 'kiosk',
    };
    analyticsEvent(
      'book_bookito_event',
      {
        event_category: `${analyticSource[bookingSource]}:book_bookito_event`,
        event_label: eventId,
      },
      { bookingSource, ...meta }
    );
  };

  const onSubmitBooking = (
    values: BookitoBookingFormValues,
    formikHelpers: FormikHelpers<BookitoBookingFormValues>
  ) => {
    if (isEditMode) {
      if (bookingId) {
        editBooking({
          comment: values?.comment,
          priceVariants: values.priceVariants,
          numOfAdditionalParticipants:
            (values.numOfAdditionalParticipants ?? 0) >= 1
              ? values.numOfAdditionalParticipants
              : undefined,
          participants: values.participants,
          id: bookingId,
        });
      }
    } else {
      const bookedForDate =
        values?.selectedTimeSlot?.startDateTime ||
        values.bookedForDate?.toISOString() ||
        new Date().toISOString(); // might need to change it

      createBooking(
        {
          eventId,
          bookingSource,
          bookedForDate,
          priceVariants: values.priceVariants,
          comment: values.comment,
          numOfAdditionalParticipants:
            (values.numOfAdditionalParticipants ?? 0) >= 1
              ? values.numOfAdditionalParticipants
              : undefined,
          participants: values.participants ?? [],
          chosenEmployeeId: values.selectedTimeSlot?.employeeId,
        },
        {
          onSuccess: () => {
            logAnalyticEvent({
              bookedForDate,
              priceVariants: values.priceVariants,
            });
            onCreateSuccess?.();
          },
        }
      );
    }
  };

  const resetForm = () => {
    formikRef.current?.resetForm();
    setAllParticipants([]);
    setIsGuestLoggedIn(false);
  };

  const resetMutation = () => {
    resetCreateMutation();
    resetEditBooking();
  };

  const isError = isCreateError || editIsError;
  const isLoading = isCreatingBooking || editIsLoading;
  const error = createError || editError;
  const isSuccess = isCreateSuccess || editIsSuccess;

  const isEditingCarlitoHubUserBooking =
    isEditMode && Boolean(initialEditData?.participants?.[0].carlitoHubUserId);

  useEffect(() => {
    if (isEditingCarlitoHubUserBooking) {
      setAllParticipants(
        initialEditData?.participants?.map((guest) => ({
          id: `${guest.carlitoHubUserId}`,
          isMainBooker: guest.isMainBooker,
          displayName: `${guest.name}`,
        })) ?? []
      );
    }
  }, [isEditingCarlitoHubUserBooking, initialEditData]);

  return {
    state: {
      isEditingCarlitoHubUserBooking,
      isEditMode,
      isError,
      successMsg,
      allParticipants,
      isLoading,
      isSubmitting: isLoading,
      error,
      isSuccess,
      initialValues,
      currentStep,
      hasPmsEnabled,
      isBookingRefEnabled: supportedPmsFeatures?.bookingReference,
      isRoomNrEnabled: supportedPmsFeatures?.roomNr,
      fieldError,
      isGuestLoggedIn,
      steps,
      formikRef,
    },
    actions: {
      setSteps,
      setFieldError,
      onSubmitBooking,
      setIsGuestLoggedIn,
      resetCurrentStep,
      setCurrenStep,
      updateSuccessMsg,
      resetMutation,
      setAllParticipants,
      resetForm,
    },
  };
};

export type BookingFormStore = ReturnType<typeof useBookingForm>;
